import { formatString } from '../bot/lib';

export type Validator<T extends any = any, F extends any = any, CTX extends any = any> = (value: T, form: F, ctx?: CTX) => boolean|Promise<boolean>

export type ValidationSuite<T extends Record<any, any> = any, CTX = any> = {
  [k in keyof T]?: Array<Validator<T[k], T, CTX>>;
} & {_global?: Array<Validator<unknown, T, CTX>>;}

export interface ValidationResult<T extends Record<string, unknown>> {
  valid: boolean;
  errors: { [k in keyof T]?: string[] }
}

export interface ValidationOptions {
  /**
   * Join the list of failed validations.
   */
  join?: boolean | ((errors: string[]) => string);
  /**
   * Executions of validation for list of rules in suite for each key.
   */
  mode?: 'serial' | 'parallel';
}

/**
 * Create a validator that throws error when the condition is false.
 * @param validator The validator
 * @param message The message to check
 * @returns A validator function
 */
export function c <T extends any = any, F extends any = any, CTX extends Record<string|number, string|number|{ toString: () => string }> = any> (validator: (value: T, form: F, ctx?: CTX) => any|Promise<any>, message: string) {
  return async (value: T, form: F, ctx?: CTX) => {
    try {
      const valid = await validator(value, form, ctx);
      if (!valid) {
        throw formatString(message, { ...(ctx), value: value as any });
      }
      return valid;
    } catch (e) {
      throw formatString(message, { ...(ctx), value: value as any });
    }
  };
}

/**
 * Validate the value of the object based on the validation suite. Optionally provide context to the validators.
 * @param form The form value to validate
 * @param suite The validation suite
 * @param ctx The context to pass to validators.
 * @returns Validation information.
 */
export async function validateForm<T extends Record<string, unknown>, CTX = any> (form: T, suite: ValidationSuite<T, CTX>, ctx?: CTX, { join = true, mode = 'serial' }: ValidationOptions = {}) {
  const validations = await Promise.all(Object.keys(suite).map(async (k) => {
    const key = k as keyof ValidationSuite<T, CTX>;
    const validators = suite[key];
    if (validators && Array.isArray(validators)) {
      const truth = Symbol('truth-value');
      if (mode === 'serial') {
        const result: Symbol | string = await (validators as any[]).reduce(async (acc: Promise<typeof truth|string>, validator: Validator) => {
          const result = await acc;
          if (result === truth) {
            return (Promise.resolve(validator(key === '_global' ? form : form[key], form, { ...(ctx as any), key }))
              .then(val => {
                if (val) {
                  return truth;
                }
                throw new Error('Validation Error');
              })
              .catch(err => `${err}`));
          }
          return result;
        }, Promise.resolve(truth));
        return [key, result === truth ? [] : [result]] as const;
      }
      const currentValidations = (validators as any[]).map(validator => Promise.resolve(validator(key !== '_global' ? form[key] : form as any, form, { ...(ctx as any), key })).catch((m: string) => `${m}`));
      return Promise.all(currentValidations).then(res => [key, res.filter(err => !!err)] as const);
    }
    return [key, []] as const;
  }));
  let entries: any[] = validations.filter(([, v]) => v && v.length);
  if (join) {
    const joiner = typeof join === 'function' ? join : (s: string[]) => s.join('\n');
    entries = entries.map(([k, v]) => [k, joiner(v)]);
  }
  const errors = Object.fromEntries(entries) as { [k in keyof T]?: Array<string> };

  return {
    valid: !entries.length,
    errors,
  } as ValidationResult<T>;
}

export function required (value: unknown) {
  return !!value;
}

export function all <T extends any = any, F extends any = any, CTX extends any = any> (validators: Validator<T, F, CTX>[], { type = 'serial' }: {type?: 'serial' | 'parallel'} = {}) {
  const toBool = (res: unknown) => !!res;
  const getFalse = () => false;
  const validator: Validator<T, F, CTX> = async (v, f, c) => {
    let result: boolean;
    if (type === 'serial') {
      result = await validators.reduce(async (acc, current) => {
        const valid = await acc;
        if (valid) {
          return Promise.resolve(current(v, f, c)).then(toBool).catch(getFalse);
        }
        return false;
      }, Promise.resolve(true as any));
    } else {
      result = await Promise.all(validators.map(validator => Promise.resolve(validator(v, f, c)).then(toBool).catch(getFalse))).then(res => res.reduce((a, c) => (a && c)));
    }

    if (!result) {
      throw new Error('Validation Failed');
    }
    return result;
  };

  return validator;
}
